import { Fragment, memo, useCallback, useEffect, useState } from 'react'
import { SxProps, Link as MuiLink, ButtonBase } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { useAnalytics } from '@/core/analytics'
import { usePackageInfo, useDomainInfo } from '@/utils'

import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'

import Popper from '../../Popper'

import { NavigationMenuDetailsProps, Item } from './NavigationMenu.types'

import {
  navigationMenuStyles as styles,
  navigationMenuDetailsStyles,
} from './NavigationMenu.styles'
import { NAVIGATION_LINK_NAME, NavigationData, navigationItems } from './NavigationMenu.data'
import { useNavigationMenuController } from './NavigationMenu.controller'
import { Partners } from './Partners'
import { globalClasses } from '@/styles'

const NavigationMenu = () => {
  const { isInsidePackageFlow } = usePackageInfo()
  const { isFanaticsDomain } = useDomainInfo()
  const { itemRefs, anchorEl, setAnchorEl, selectedNavigationItem, onNavigationSelection } =
    useNavigationMenuController()

  if (isInsidePackageFlow) return null

  return (
    <Box
      sx={
        [
          navigationMenuDetailsStyles.navigation,
          isFanaticsDomain && navigationMenuDetailsStyles.navigationFanatics,
        ] as SxProps
      }
      onMouseLeave={() => {
        onNavigationSelection('')
        setAnchorEl(null)
      }}>
      {navigationItems.map((navItem) => (
        <Fragment key={navItem.label}>
          <ButtonBase
            ref={(el) => {
              itemRefs.current[navItem.label] = el
            }}
            sx={navigationMenuDetailsStyles.navigationItem}
            onClick={() => onNavigationSelection(navItem.label)}
            onMouseEnter={() => onNavigationSelection(navItem.label)}>
            <Text sx={navigationMenuDetailsStyles.navigationText}>{navItem.label}</Text>
          </ButtonBase>
          {selectedNavigationItem !== NAVIGATION_LINK_NAME.PARTNERS &&
            selectedNavigationItem === navItem.label && (
              <Popper open sx={styles.container} anchorEl={anchorEl} placement="bottom-start">
                <NavigationMenuDetails
                  data={NavigationData[navItem.label] || []}
                  onNavigationSelection={onNavigationSelection}
                />
              </Popper>
            )}
        </Fragment>
      ))}

      {selectedNavigationItem === NAVIGATION_LINK_NAME.PARTNERS && (
        <Partners anchorEl={selectedNavigationItem ? anchorEl : null} />
      )}
    </Box>
  )
}

export default memo(NavigationMenu)

const NavigationMenuDetails = ({ data, onNavigationSelection }: NavigationMenuDetailsProps) => {
  const items: Item[] = data[0].items
  const [selectedItem, setSelectedItem] = useState<Item | null>()

  const analytics = useAnalytics()

  const onLinkClick = useCallback(
    (navText: string, navButton: string) => () => {
      onNavigationSelection('')
      analytics.sendNavigationEvent(navText, `${navText}.${navButton}`)
    },
    [analytics, onNavigationSelection],
  )

  useEffect(() => {
    /**
     * if children column is opened and user select different
     * navigation item so previously selected children item should be changed
     */

    setSelectedItem(data[0].items[0])
  }, [data])

  return (
    <Box sx={navigationMenuDetailsStyles.navigationMenuDetailWrapper}>
      <Box sx={navigationMenuDetailsStyles.itemList}>
        <Text sx={navigationMenuDetailsStyles.columnHeaderName}>{data[0].name}</Text>
        <>
          {items.map((currentItem) => (
            <MuiLink
              component={RouterLink}
              to={currentItem.link}
              onMouseEnter={() => setSelectedItem(currentItem)}
              key={currentItem.name}
              sx={
                [
                  navigationMenuDetailsStyles.itemContainer,
                  currentItem.name === selectedItem?.name &&
                    navigationMenuDetailsStyles.selectedItem,
                ] as SxProps
              }>
              <Text sx={navigationMenuDetailsStyles.itemName}>{currentItem.name}</Text>
            </MuiLink>
          ))}
        </>
      </Box>

      {selectedItem && selectedItem.children.length > 0 && (
        <Box sx={navigationMenuDetailsStyles.childrenItemList}>
          <>
            <Text sx={navigationMenuDetailsStyles.columnHeaderName}>{selectedItem.name}</Text>
            <Box>
              {selectedItem.children.map((childItem) => (
                <MuiLink
                  component={RouterLink}
                  to={childItem.link}
                  onClick={onLinkClick(selectedItem.name, childItem.text)}
                  key={childItem.text}
                  sx={navigationMenuDetailsStyles.childrenItemContainer}>
                  {childItem.primaryColor && (
                    <Box
                      sx={{
                        ...navigationMenuDetailsStyles.childrenItemColorBar,
                        backgroundColor: childItem.primaryColor,
                      }}
                    />
                  )}

                  <Text
                    className={globalClasses.ellipsis}
                    sx={navigationMenuDetailsStyles.itemName}>
                    {childItem.text}
                  </Text>
                </MuiLink>
              ))}
            </Box>
          </>
        </Box>
      )}
    </Box>
  )
}
